// src/components/ChargingStation/PaymentMethod.web.tsx
import React from 'react';
import {
  View,
  Text,
  Pressable,
  Image,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';

// Import assets for web compatibility
import creditCardIcon from '../../../assets/creditCardIcon.png';
import { MaterialIcons } from '@expo/vector-icons';
import StripeSubmitButton from '../payments/StripeSubmitButton';
import LoadingPlaceholder from '../LoadingPlaceholder';
import { ActiveChargeAttributes } from '../../models/active-charges-model';

interface PaymentMethodWebProps {
  renderChargerIsFree: boolean;
  activeCharges: ActiveChargeAttributes[] | null;
  order: any;
  paymentData: any;
  paymentsDropdownOpen: boolean;
  onDropdownPressed: () => void;
  handleRemovePayment: () => void;
  onPaymentSubmit: () => Promise<void>;
  stripeElements?: any;
  loadError: boolean;
}

const PaymentMethodWeb: React.FC<PaymentMethodWebProps> = ({
  renderChargerIsFree,
  activeCharges,
  order,
  paymentData,
  paymentsDropdownOpen,
  onDropdownPressed,
  handleRemovePayment,
  onPaymentSubmit,
  stripeElements,
  loadError,
}) => {
  return (
    <>
      <View style={styles.lin2} className="py-1" />
      {!renderChargerIsFree && !activeCharges && order ? (
        <View className="flex flex-col mx-4">
          <Text className="py-2 text-2xl font-bold text-white">
            Payment method
          </Text>
          <Pressable
            className="flex flex-row justify-between items-center my-5"
            onPress={onDropdownPressed}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
            disabled={paymentData !== null && paymentData !== undefined}
          >
            <Image
              source={creditCardIcon}
              style={{ height: 34, width: 50 }}
              alt="CreditCard"
            />
            <View className="flex flex-col justify-between items-start">
              <Text
                className="text-lg font-semibold text-white"
                style={{ textAlign: 'center', flex: 1 }}
              >
                {paymentData ? 'Change payment method' : 'Add a payment method'}
              </Text>
              {paymentData && (
                <Text className="text-base text-slate-400">
                  Card ending in {` ${paymentData.lastFour}`}
                </Text>
              )}
            </View>

            {paymentData ? (
              <Pressable onPress={handleRemovePayment}>
                <MaterialIcons name="close" size={24} color="white" />
              </Pressable>
            ) : (
              <MaterialIcons
                name="keyboard-arrow-right"
                size={24}
                color="white"
              />
            )}
          </Pressable>

          <View
            id="address-element"
            style={{ display: paymentsDropdownOpen ? 'flex' : 'none' }}
            className="flex z-40 flex-col my-5"
          />

          <View
            id="payment-element"
            style={{ display: paymentsDropdownOpen ? 'flex' : 'none' }}
            className="flex z-10 flex-col my-5"
          />

          {/* Show helpful message when user has existing payment method but dropdown is closed */}
          {paymentData && !paymentsDropdownOpen && (
            <View className="flex flex-col my-5">
              <Text className="mb-3 text-base text-slate-300">
                Your payment method is ready. Click "Change payment method"
                above to review and submit.
              </Text>
            </View>
          )}
        </View>
      ) : (
        !activeCharges && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#39BEB7" />
          </View>
        )
      )}

      {paymentsDropdownOpen && !activeCharges && (
        <StripeSubmitButton
          onSubmit={async () => {
            if (stripeElements) {
              return await stripeElements
                .submit()
                .then(onPaymentSubmit)
                .catch((error: any) => {
                  console.error('Error during submission: ' + error);
                });
            }
            return Promise.resolve();
          }}
        />
      )}

      {loadError && !order && !renderChargerIsFree && (
        <View className="flex flex-row justify-start items-center px-4">
          <MaterialIcons name="close" size={50} color="#AA1111" />
          <View className="flex flex-col justify-center items-center">
            <Text className="mx-16 text-lg font-semibold text-center text-white">
              Something went wrong
            </Text>
            <Text className="mx-16 text-base text-center text-slate-400">
              Please refresh the page
            </Text>
          </View>
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  lin2: {
    borderBottomColor: '#438b88',
    borderBottomWidth: 1,
    marginVertical: 10,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0B1E1D',
  },
});

export default PaymentMethodWeb;
